import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import {
  isStaleAppBundleLoadError,
  markChunkRecoveryReloadAttempt,
  shouldAttemptChunkRecoveryReload,
} from '../../lib/chunkLoadError'
import { captureSentryException } from '../../lib/sentry'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary:', error, errorInfo)
    if (isStaleAppBundleLoadError(error)) {
      if (shouldAttemptChunkRecoveryReload()) {
        markChunkRecoveryReloadAttempt()
        window.location.reload()
      }
      return
    }
    captureSentryException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    })
  }

  handleRetry = (): void => {
    const err = this.state.error
    if (err && isStaleAppBundleLoadError(err)) {
      window.location.reload()
      return
    }
    this.setState({ hasError: false, error: null })
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) return this.props.fallback
      const chunkStale = isStaleAppBundleLoadError(this.state.error)
      return (
        <div
          className="flex min-h-[200px] flex-col items-center justify-center gap-4 rounded-lg border border-red-200 bg-red-50 p-8 text-red-800 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200"
          role="alert"
        >
          <AlertTriangle className="h-12 w-12 shrink-0" aria-hidden />
          <p className="text-center font-medium">
            {chunkStale
              ? 'De site is intussen vernieuwd of de verbinding werd onderbroken. Vernieuw de pagina om verder te gaan.'
              : 'Er is iets misgegaan. Probeer de pagina te vernieuwen.'}
          </p>
          <p className="text-center text-sm opacity-80">{this.state.error.message}</p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            {chunkStale ? 'Pagina vernieuwen' : 'Opnieuw proberen'}
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
